import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import cn from 'classnames';
import emailMask from 'text-mask-addons/dist/emailMask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import faker from 'faker';

import Validator from 'components/Validator/Validator';
import Header from 'components/Header';
import Footer from 'components/Footer';
import FormGroup from 'components/Form/FormGroup';
import FormGroupLabel from 'components/FormGroupLabel';
import LoanAmountRequested from 'components/LoanAmountRequested';
import Input from 'components/Form/Input';
import Select from 'components/Form/Select';
import Checkbox from 'components/Form/Checkbox';
import { Button } from 'components/Button';
import Sidebar from 'components/Sidebar';

import { parseUrlParams } from 'utils/parseUrlParams';

import {
  checkinAction,
  checkPreviousAction,
} from 'actions/workflow';

import schema from './schema';
import style from './style.scss';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
  integerLimit: 5,
});
const unmask = val => val.replace(/[$, ]+/g, '');
const Application = ({
  className,
  history,
  validator: { validate, values, errors, setValues, onChangeHandler },
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentDate = () => format(new Date(), 'M/dd/yyyy');

  useEffect(() => {
    const initialFormData = {
      requestedAmount: 1000,
      submittedDate: getCurrentDate(),
      isSelfSubmitted: true,
      merchant: {
        name: 'Dr.John Smith',
        phone: '111-111-1111',
      },
      purpose: 'Plastic Surgery',
      channel: {
        code: 'merchant',
        name: 'merchant',
      },
    };
    setValues(initialFormData);

    const params = parseUrlParams(window.location.search);
    if (params.applicationId) {
      dispatch(checkPreviousAction({
        data: {},
        url: `/workflows/application/${params.applicationId}/workflow/dtm/checkin`,
        success: (response) => {
          const routeUrl = response.state && response.state.url;
          history.push(routeUrl);
        },
        fail: () => {},
      }));
    }
  }, []);

  const handleSubmitFrom = (data, e) => {
    e.preventDefault();
    if (validate(schema).isValid) {
      const formData = {
        ...data,
        applicant: {
          ...data.applicant,
          addresses: [{
            ...data.applicant.addresses,
            country: 'USA',
          }],
          phoneNumbers: [
            {
              Type: 'Mobile',
              Number: data.applicant.phoneNumbers.Number,
            },
          ],
        },
        financials: {
          stated: {
            ...data.financials.stated,
            source: 'application',
            grossMonthlyIncome: unmask(data.financials.stated.grossMonthlyIncome),
            monthlyRentOrMortage: unmask(data.financials.stated.monthlyRentOrMortage),
          },
        },
      };

      setIsLoading(true);

      dispatch(checkinAction({
        data: formData,
        url: '/workflows/application/workflow/dtm/start',
        success: (response) => {
          setIsLoading(false);
          history.push(`/dashboard/borrowers-merchant/congratulations?applicationId=${response.entityId}`);
        },
        fail: () => {},
      }));
    }
  };

  const handleInputChange = (event) => {
    onChangeHandler(event.target.name, event.target.value);
  };

  const handleCheckboxChange = (name, value) => {
    onChangeHandler(name, value);
  };

  const handleSliderChange = (value) => {
    onChangeHandler('requestedAmount', value);
  };

  const handleSetValues = (e) => {
    e.preventDefault();
    const formValues = {
      requestedAmount: 4320,
      channel: {
        code: 'merchant',
        name: 'merchant',
      },
      applicant: {
        firstName: faker.name.firstName(),
        lastName: faker.name.lastName(),
        addresses: {
          address1: '4901 202 Street East',
          city: 'Spanaway',
          state: 'alabama',
          zipcode: '98387',
        },
        dateOfBirth: '02/20/1974',
        ssn: '686187684',
        email: 'john.doe+9010@lendingusa.com',
        phoneNumbers: {
          Number: '(919) 780-8674',
        },
      },
      financials: {
        stated: {
          grossMonthlyIncome: '145000',
          rentOrOwn: 'rent',
          monthlyRentOrMortage: '23232',
          employmentStatus: 'Employed',
          employerName: 'John',
          employerPhone: '(111) 111-1111',
          employmentYears: '6',
        },
      },
      isSelfSubmitted: true,
      hasApplicationConsented: true,
      signatureBy: {
        name: 'John',
      },
      submittedDate: '3/28/2018',
    };
    setValues(formValues);
  };

  return (
    <form className={cn(style.App, className)} onSubmit={handleSubmitFrom.bind(null, values)}>
      <Header />
      <Button
        className={cn('button small w-50', style.button)}
        onClick={handleSetValues}
      >
        Set values
      </Button>
      <section className="container section">
        <div className="grid-container fluid borrowers-apply">
          <div className="grid-container">
            <div className="grid-x max-limited" style={{ marginBottom: '40px' }}>
              <div className="cell small-12 medium-12">
                <h2>Check Your Rate</h2>
                <p className="p-large">Checking your rate takes seconds and won&apos;t impact your credit score✝</p>
              </div>
            </div>

            <div className="grid-x max-limited">
              {
                <Fragment>
                  <div className="cell small-12 large-8 main-column">
                    <FormGroup className={cn('form-group', style.LoanAmountRequested)}>
                      <LoanAmountRequested
                        amount={values.requestedAmount}
                        onChange={handleSliderChange}
                        style={{ marginTop: '40px' }}
                      />
                    </FormGroup>

                    <div className="form-group">
                      <FormGroupLabel value="Personal Information" />
                      <div className="grid-x grid-margin-x">
                        <div className="cell small-12 medium-6">
                          <p>Please provide the legal name and personal identity information for the primary borrower.</p>
                        </div>
                        <div className="cell small-12 medium-6">
                          <p>Enter the address of the primary borrower above. Must be a valid street address, no P.O. Boxes.</p>
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="First Name"
                            name="applicant.firstName"
                            value={values.applicant && values.applicant.firstName}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.firstName']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Last Name"
                            name="applicant.lastName"
                            value={values.applicant && values.applicant.lastName}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.lastName']}
                          />
                        </div>
                        <div className="cell small-12 medium-12">
                          <Input
                            label="Street Address"
                            name="applicant.addresses.address1"
                            value={values.applicant && values.applicant.addresses && values.applicant.addresses.address1}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.addresses.address1']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="City"
                            name="applicant.addresses.city"
                            value={values.applicant && values.applicant.addresses && values.applicant.addresses.city}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.addresses.city']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <div className="grid-x grid-margin-x">
                            <div className="cell small-8">
                              <Select
                                name="applicant.addresses.state"
                                data={[
                                  { value: 'alabama', title: 'Alabama' },
                                  { value: 'alaska', title: 'Alaska' },
                                  { value: 'arizona', title: 'Arizona' },
                                  { value: 'arkansas', title: 'Arkansas' },
                                  { value: 'california', title: 'California' },
                                  { value: 'colorado', title: 'Colorado' },
                                  { value: 'connecticut', title: 'Connecticut' },
                                  { value: 'delaware', title: 'Delaware' },
                                  { value: 'district-of-columbia', title: 'District of Columbia' },
                                  { value: 'florida', title: 'Florida' },
                                  { value: 'georgia', title: 'Georgia' },
                                  { value: 'hawaii', title: 'Hawaii' },
                                  { value: 'idaho', title: 'Idaho' },
                                  { value: 'illinois', title: 'Illinois' },
                                  { value: 'indiana', title: 'Indiana' },
                                  { value: 'iowa', title: 'Iowa' },
                                  { value: 'kansas', title: 'Kansas' },
                                  { value: 'kentucky', title: 'Kentucky' },
                                  { value: 'louisiana', title: 'Louisiana' },
                                  { value: 'maine', title: 'Maine' },
                                  { value: 'maryland', title: 'Maryland' },
                                  { value: 'massachusetts', title: 'Massachusetts' },
                                  { value: 'michigan', title: 'Michigan' },
                                  { value: 'minnesota', title: 'Minnesota' },
                                  { value: 'mississippi', title: 'Mississippi' },
                                  { value: 'missouri', title: 'Missouri' },
                                  { value: 'montana', title: 'Montana' },
                                  { value: 'nebraska', title: 'Nebraska' },
                                  { value: 'nevada', title: 'Nevada' },
                                  { value: 'new-hampshire', title: 'New Hampshire' },
                                  { value: 'new-jersey', title: 'New Jersey' },
                                  { value: 'new-mexico', title: 'New Mexico' },
                                  { value: 'new-york', title: 'New York' },
                                  { value: 'north-carolina', title: 'North Carolina' },
                                  { value: 'north-dakota', title: 'North Dakota' },
                                  { value: 'ohio', title: 'Ohio' },
                                  { value: 'oklahoma', title: 'Oklahoma' },
                                  { value: 'oregon', title: 'Oregon' },
                                  { value: 'pennsylvania', title: 'Pennsylvania' },
                                  { value: 'rhode-island', title: 'Rhode Island' },
                                  { value: 'south-carolina', title: 'South Carolina' },
                                  { value: 'south-dakota', title: 'South Dakota' },
                                  { value: 'tennessee', title: 'Tennessee' },
                                  { value: 'texas', title: 'Texas' },
                                  { value: 'utah', title: 'Utah' },
                                  { value: 'vermont', title: 'Vermont' },
                                  { value: 'virginia', title: 'Virginia' },
                                  { value: 'washington', title: 'Washington' },
                                  { value: 'west-virginia', title: 'West Virginia' },
                                  { value: 'wisconsin', title: 'Wisconsin' },
                                  { value: 'wyoming', title: 'Wyoming' },
                                ]}
                                value={values.applicant && values.applicant.addresses && values.applicant.addresses.state}
                                onChange={handleInputChange}
                                label="State"
                                isRequired
                                hasError={!!errors['applicant.addresses.state']}
                              />
                            </div>
                            <div className="cell small-4">
                              <Input
                                label="Zip"
                                isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                                name="applicant.addresses.zipcode"
                                value={values.applicant && values.applicant.addresses && values.applicant.addresses.zipcode}
                                onChange={handleInputChange}
                                isRequired
                                hasError={!!errors['applicant.addresses.zipcode']}
                              />
                            </div>
                          </div>
                          <div id="ResidentAlert" className="grid-x grid-margin-x hide">
                            <div className="cell small-12">
                              <div className="alert callout">
                                <p>We currently do not accept loan applications for residents of the state of <span className="state">state</span>.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Date of Birth"
                            name="applicant.dateOfBirth"
                            // eslint-disable-next-line
                            isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                            placeHolder="__/__/____"
                            value={values.applicant && values.applicant.dateOfBirth}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.dateOfBirth']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Social Security #"
                            name="applicant.ssn"
                            // eslint-disable-next-line
                            isMasked={[/\d/,/\d/,/\d/, '-', /\d/,/\d/, '-',/\d/,/\d/,/\d/,/\d/]}
                            placeHolder="___-__-____"
                            value={values.applicant && values.applicant.ssn}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.ssn']}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <FormGroupLabel value="Contact Information" />
                      <p>Loan and application status will be sent to the following.</p>
                      <div className="grid-x grid-margin-x">
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Email Address"
                            name="applicant.email"
                            isMasked={emailMask}
                            placeHolder="Email address"
                            value={values.applicant && values.applicant.email}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.email']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Mobile Phone"
                            name="applicant.phoneNumbers.Number"
                            placeHolder="(___) ___-____"
                            isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            value={values.applicant && values.applicant.phoneNumbers && values.applicant.phoneNumbers.Number}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['applicant.phoneNumbers.Number']}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <FormGroupLabel value="Financials &amp; Employment" />
                      <p>Your total verifiable gross income might include your salary, retirement income or other sources of income. Alimony, child support or seperate maintenance income are optional.</p>
                      <div className="grid-x grid-margin-x">
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Gross Monthly Income"
                            name="financials.stated.grossMonthlyIncome"
                            value={values.financials && values.financials.stated && `${values.financials.stated.grossMonthlyIncome}`}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['financials.stated.grossMonthlyIncome']}
                            isMasked={currencyMask}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Select
                            name="financials.stated.rentOrOwn"
                            data={[
                              { value: 'own-home', title: 'Own Home' },
                              { value: 'rent', title: 'Rent' },
                              { value: 'live-with-parents', title: 'Live with parents' },
                              { value: 'other', title: 'Other' },
                            ]}
                            value={values.financials && values.financials.stated && values.financials.stated.rentOrOwn}
                            onChange={handleInputChange}
                            label="Rent/Own"
                            isRequired
                            hasError={!!errors['financials.stated.rentOrOwn']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Monthly Rent/Mortgage"
                            name="financials.stated.monthlyRentOrMortage"
                            value={values.financials && values.financials.stated && `${values.financials.stated.monthlyRentOrMortage}`}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['financials.stated.monthlyRentOrMortage']}
                            isMasked={currencyMask}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Select
                            name="financials.stated.employmentStatus"
                            data={[
                              { value: 'Employed', title: 'Employed' },
                              { value: 'rent-employed', title: 'Self-Employed' },
                              { value: 'rent-unemployed', title: 'Unemployed' },
                              { value: 'rent-student', title: 'Student' },
                            ]}
                            value={values.financials && values.financials.stated && values.financials.stated.employmentStatus}
                            onChange={handleInputChange}
                            label="Employment Status"
                            isRequired
                            hasError={!!errors['financials.stated.employmentStatus']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            label="Employer Name"
                            name="financials.stated.employerName"
                            value={values.financials && values.financials.stated && values.financials.stated.employerName}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['financials.stated.employerName']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Input
                            name="financials.stated.employerPhone"
                            isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            label="Employer Phone"
                            placeHolder="(___) ___-____"
                            value={values.financials && values.financials.stated && values.financials.stated.employerPhone}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['financials.stated.employerPhone']}
                          />
                        </div>
                        <div className="cell small-12 medium-6">
                          <Select
                            name="financials.stated.employmentYears"
                            data={[
                              { value: '6', title: 'Less than 6 months' },
                              { value: '6-12', title: '6 months – 1 year' },
                              { value: '12-24', title: '1–2 Years' },
                              { value: '24-36', title: '2–3 Years' },
                              { value: '36', title: '3+ Years' },
                            ]}
                            value={values.financials && values.financials.stated && values.financials.stated.employmentYears}
                            onChange={handleInputChange}
                            label="Years with Current Employer"
                            isRequired
                            hasError={!!errors['financials.stated.employmentYears']}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <FormGroupLabel value="Your Signature" />
                      <div className="grid-x grid-margin-x">
                        <div className="cell small-12 large-8 padded-bottom">
                          <h6>Is the applicant above the person receiving the product/services?</h6>
                        </div>
                        <div className={cn('cell small-12 large-4 padded-bottom padded-bottom-small', style.signatureConfirm)}>
                          <Checkbox
                            label={['Yes']}
                            name="isSelfSubmitted"
                            onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', true)}
                            isChecked={values.isSelfSubmitted}
                            value="on"
                            id="isSelfSubmitted"
                            type="radio"
                          />
                          <Checkbox
                            label={['No']}
                            name="isSelfSubmittedNo"
                            onChange={handleCheckboxChange.bind(null, 'isSelfSubmitted', false)}
                            isChecked={!values.isSelfSubmitted}
                            id="isSelfSubmittedNo"
                            value="off"
                            type="radio"
                          />
                        </div>
                        <div className="cell small-12">
                          <h6>By checking the box below I agree to the following:</h6>
                        </div>
                        <div className="cell small-12 checkbox-row padded-bottom">
                          <Checkbox
                            label={[<a href="#electronic" data-toggle="Modal_E_Comm">Electronic Consent</a>, ',', <a href="#electronic" data-toggle="Modal_E_Comm">Terms of Use &amp; Privacy Policy</a>, ',', <a href="#electronic" data-toggle="Modal_E_Comm">Request for Credit</a>]}
                            name="hasApplicationConsented"
                            onChange={handleCheckboxChange.bind(null, 'hasApplicationConsented')}
                            isChecked={values.hasApplicationConsented}
                            id="hasApplicationConsented"
                            errorMessage={errors.hasApplicationConsented}
                          />
                        </div>
                        <div className="cell small-12 medium-9 padded-top padded-bottom">
                          <Input
                            label="Applicant E-Signature (Type full name)"
                            name="signatureBy.name"
                            value={values.signatureBy && values.signatureBy.name}
                            onChange={handleInputChange}
                            isRequired
                            hasError={!!errors['signatureBy.name']}
                          />
                        </div>
                        <div className="cell small-12 medium-3 padded-top padded-bottom">
                          <Input
                            name="submittedDate"
                            value={values.submittedDate}
                            label="Date"
                            className="current-date"
                            isDisabled
                          />
                        </div>
                      </div>

                      <h5>Important information about procedures for opening a new account</h5>
                      <textarea className="no-resize" rows="3" readOnly defaultValue="To help the government fight the funding of terrorism and money laundering activities, federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account or applies for a loan from Cross River Bank (&ldquo;we&rdquo;). What this means for you: When you open an account or apply for a loan, we will ask for your name, address, date of birth and other information that will allow us to identify you. We may also ask to see your driver’s license or other identifying documents. If you fail to provide the required information, we may be unable to open an account or grant you a loan." />
                      <Button
                        className={cn('button large green w-100', style.button, isLoading ? '' : 'arrow')}
                        onClick={handleSubmitFrom.bind(null, values)}
                        isLoading={isLoading}
                      >
                        Check My Rate
                      </Button>
                    </div>
                  </div>
                </Fragment>
              }

              <Sidebar />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </form>
  );
};

Application.propTypes = {
  className: PropTypes.string,
  validator: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

Application.defaultProps = {
  className: '',
};

export default Validator(schema)(Application);



// WEBPACK FOOTER //
// ./src/containers/pages/BorrowerMerchantApply/InternalPages/Application/index.js